
import { defineComponent } from 'vue';
import type { PropType } from 'vue';
import CopyURLToClipboard from './CopyURLToClipboard.vue';
import FeedbackForm from './FeedbackForm.vue';
import { ISharingInfo } from '../helpers/sharing-modal';

export default defineComponent({
	name: 'SharingModal',
	components: { CopyURLToClipboard, FeedbackForm },
	beforeDestroy() {
		// make sure to hide modal so it doesn't remain open when navigating back from insidepage
		this.$modal.hide('sharingmodal');
	},
	data() {
		return {
			feedbackFormOpened: false,
			sharingOptionUsed: false,
			revealFullLink: false
		};
	},
	computed: {
		sharingOptions(): { name: string; icon: string; text: string; link: string }[] {
			return [
				{
					name: 'email',
					icon: 'icon-letter-empty',
					text: 'Per E-Mail verschicken',
					link: `mailto:?subject=${
						this.sharingInfo.jobName
							? `Job-Empfehlung für dich: ${this.sharingInfo.jobName}`
							: `Schau dir dieses Unternehmen an${
									this.sharingInfo.companyName ? `: ${this.sharingInfo.companyName}` : ''
								}`
					}&body=${this.mailBody}`
				},
				{
					name: 'linkedin',
					icon: 'icon-linkedin-2',
					text: 'Auf LinkedIn teilen',
					link: `https://www.linkedin.com/shareArticle?url=${this.sharingInfo.link}`
				},
				{
					name: 'facebook',
					icon: 'icon-fb',
					text: 'Auf Facebook teilen',
					link: `https://www.facebook.com/sharer.php?u=${this.sharingInfo.link}`
				},
				{
					name: 'whatsapp',
					icon: 'icon-whatsapp',
					text: 'Per WhatsApp verschicken',
					link: `${this.$isMobile.any ? 'whatsapp://' : 'https://web.whatsapp.com/'}send?text=${
						this.sharingInfo.link
					}`
				}
			];
		},
		sharingOptionsWebsite(): { name: string; icon: string; text: string; link: string }[] {
			return [
				{
					name: 'whatsapp',
					icon: 'icon-whatsapp',
					text: 'Per WhatsApp verschicken',
					link: `${this.$isMobile.any ? 'whatsapp://' : 'https://web.whatsapp.com/'}send?text=${
						this.sharingInfo.link
					}`
				},
				{
					name: 'email',
					icon: 'icon-letter-empty',
					text: 'Per E-Mail verschicken',
					link: `mailto:?subject=${
						this.sharingInfo.jobName
							? `Job-Empfehlung für dich: ${this.sharingInfo.jobName}`
							: `Schau dir dieses Unternehmen an${
									this.sharingInfo.companyName ? `: ${this.sharingInfo.companyName}` : ''
								}`
					}&body=${this.mailBody}`
				},
				{
					name: 'linkedin',
					icon: 'icon-linkedin-2',
					text: 'Auf LinkedIn teilen',
					link: `https://www.linkedin.com/shareArticle?url=${this.sharingInfo.link}`
				},
				{
					name: 'facebook',
					icon: 'icon-fb',
					text: 'Auf Facebook teilen',
					link: `https://www.facebook.com/sharer.php?u=${this.sharingInfo.link}`
				}
			];
		},
		sharingMode(): { name: string; icon: string; text: string; link: string }[] {
			if (this.websiteMode) {
				return this.sharingOptionsWebsite;
			}
			return this.sharingOptions;
		},
		buttonHtml(): string {
			return `<a href="${this.sharingInfo.link}" target="_blank" style="padding:7px 17px!important;border-radius:4px!important;color:#fff!important;background-color:#0fb1af!important;text-decoration:none!important;font-weight:bold!important;" >Bewerbung starten</a>`;
		},
		mailBody(): string {
			return encodeURIComponent(
				`${this.sharingInfo.salutation}\n${this.sharingInfo.jobName || ''}\n${
					this.sharingInfo.companyName
				}${
					this.sharingInfo.jobName
						? '\nDetails: '
						: `${this.sharingInfo.details ? '\nStandort: ' : ''}`
				}${this.sharingInfo.details}\nLink: ${this.sharingInfo.link}`
			);
		},
		openModalOnMount(): boolean {
			return !!(
				this.$route.query?.openSharingModal && this.$route.query?.openSharingModal === 'true'
			);
		}
	},
	mounted() {
		if (this.openModalOnMount) {
			this.$modal.show('sharingmodal');
		}
	},
	methods: {
		triggerSharingOption(optionName: string) {
			this.$emit('sharing-modal-tracking', optionName);
			this.sharingOptionUsed = true;
		},
		copyLinkManually() {
			const selection = document.getSelection();
			if (selection && selection.toString().includes(this.sharingInfo.link)) {
				this.sharingOptionUsed = true;
				this.$emit('sharing-modal-tracking', 'copied-link-manually');
			}
		},
		copyLinkViaButton() {
			this.sharingOptionUsed = true;
			this.$emit('sharing-modal-tracking', 'copied-link-via-button');
		},
		copyButtonHTML() {
			const el = document.createElement('textarea');
			el.value = this.buttonHtml;
			el.setAttribute('readonly', '');
			el.style.cssText = 'position:fixed;pointer-events:none;z-index:-9999;opacity:0;';

			document.body.appendChild(el);
			el.select();
			document.execCommand('copy');
			document.body.removeChild(el);
			this.$snack.show({
				text: 'HTML-Button in Zwischenablage kopiert.'
			});
			this.$emit('sharing-modal-tracking', 'copied-html-button');
			this.sharingOptionUsed = true;
		},
		handleCloseModal(hideModal = true) {
			if (hideModal) {
				this.$emit(
					'sharing-modal-tracking',
					`modal-closed-${this.sharingOptionUsed ? 'used' : 'unused'}`
				);
				this.$modal.hide('sharingmodal');
				this.sharingOptionUsed = false;
			}
			this.feedbackFormOpened = false;
			this.revealFullLink = false;
		},
		sendFeedback(feedback) {
			this.$emit('send-feedback', feedback);
			this.feedbackFormOpened = false;
		}
	},
	props: {
		showSharingButton: { type: Boolean, default: true },
		title: { type: String, default: 'hokify.at - die größte mobile Job-Plattform' },
		sharingInfo: { type: Object as PropType<ISharingInfo>, default: () => {} },
		color: {
			type: String,
			default: 'main',
			validator: (value: string) => ['main', 'main-business'].includes(value)
		},
		websiteMode: { type: Boolean, default: false }
	}
});
